import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router';
import { z } from 'zod';
import { changePassword } from '@/api/account';
import { isApiError } from '@/api/errors';
import type { AdminSession } from '@/api/types';
import { SESSION_QUERY_KEY, useSession } from '@/auth/session';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Field, FieldError, FieldGroup, FieldLabel } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { PageHeader } from '@/ui/PageHeader';

const MIN_LENGTH = 12;

const schema = z
  .object({
    current_password: z.string().min(1, 'Enter your current password.'),
    new_password: z.string().min(MIN_LENGTH, `Use at least ${MIN_LENGTH} characters.`),
    repeat_password: z.string().min(1, 'Repeat the new password.'),
  })
  .refine((v) => v.new_password === v.repeat_password, {
    path: ['repeat_password'],
    message: 'The two new passwords do not match.',
  });
type Form = z.infer<typeof schema>;

const FIELDS = [
  { name: 'current_password', label: 'Current password', autoComplete: 'current-password' },
  { name: 'new_password', label: 'New password', autoComplete: 'new-password' },
  { name: 'repeat_password', label: 'Repeat new password', autoComplete: 'new-password' },
] as const;

export function ChangePasswordPage() {
  const { session } = useSession();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const forced = session?.must_change_password ?? false;

  const {
    register,
    handleSubmit,
    setError,
    formState: { errors },
  } = useForm<Form>({ resolver: zodResolver(schema) });

  const change = useMutation({
    mutationFn: (values: Form) =>
      changePassword({
        current_password: values.current_password,
        new_password: values.new_password,
      }),
    onSuccess: () => {
      queryClient.setQueryData<AdminSession | null>(SESSION_QUERY_KEY, (current) =>
        current ? { ...current, must_change_password: false } : current,
      );
      void navigate('/', { replace: true });
    },
    onError: (error) => {
      if (!isApiError(error)) return;
      for (const { field, message } of error.fieldErrors) {
        if (field === 'current_password' || field === 'new_password') setError(field, { message });
      }
    },
  });

  const fieldMessage = (error: unknown) =>
    isApiError(error) && error.fieldErrors.length === 0 ? error.message : null;
  const generalError = change.isError ? fieldMessage(change.error) : null;

  return (
    <>
      <PageHeader title="Change password" />
      <Card className="max-w-md">
        <CardHeader>
          <CardTitle>Choose a new password</CardTitle>
          {forced && (
            <CardDescription>
              Your password was set by someone else. Choose your own to continue.
            </CardDescription>
          )}
        </CardHeader>
        <CardContent>
          {generalError && (
            <Alert variant="destructive" className="mb-4">
              <AlertDescription>{generalError}</AlertDescription>
            </Alert>
          )}
          <form noValidate onSubmit={handleSubmit((values) => change.mutate(values))}>
            <FieldGroup>
              {FIELDS.map(({ name, label, autoComplete }) => (
                <Field key={name} data-invalid={Boolean(errors[name])}>
                  <FieldLabel htmlFor={name}>{label}</FieldLabel>
                  <Input
                    id={name}
                    type="password"
                    autoComplete={autoComplete}
                    aria-invalid={Boolean(errors[name])}
                    {...register(name)}
                  />
                  <FieldError errors={[errors[name]]} />
                </Field>
              ))}
              <Button type="submit" disabled={change.isPending}>
                {change.isPending ? 'Saving…' : 'Change password'}
              </Button>
            </FieldGroup>
          </form>
        </CardContent>
      </Card>
    </>
  );
}
